import React from 'react';
import { ChevronDown, ChevronUp, Search, X } from 'lucide-react';

/**
 * Kolon aramasını aç/kapat.
 * Başka kolona geçerken önceki columnSearches değerini silmez.
 * Aynı kolona tekrar basınca sadece edit modunu kapatır (filtre kalır).
 */
export const openOrToggleColumnSearch = ({
  column,
  activeColumn,
  setActiveColumn,
  searches,
  setSearches
}) => {
  if (activeColumn === column) {
    setActiveColumn(null);
    return;
  }

  setActiveColumn(column);
  if (searches?.[column] === undefined) {
    setSearches((prev) => ({ ...prev, [column]: '' }));
  }
};

/**
 * Kolon filtresini temizler ve o kolon edit'teyse kapatır.
 */
export const clearColumnSearchValue = ({
  column,
  activeColumn,
  setActiveColumn,
  setSearches
}) => {
  setSearches((prev) => {
    const next = { ...prev };
    delete next[column];
    return next;
  });

  if (activeColumn === column) {
    setActiveColumn(null);
  }
};

const normalizeLabel = (label) => {
  if (Array.isArray(label)) {
    return label.filter(Boolean).join(' ');
  }
  return String(label ?? '');
};

const alignClassMap = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right'
};

const justifyClassMap = {
  left: '',
  center: 'justify-center',
  right: 'justify-end'
};

const ColumnSearchHeaderCell = ({
  label,
  searchValue = '',
  isSearchActive = false,
  isSorted = false,
  sortDirection = 'asc',
  onSort,
  onOpenSearch,
  onClearSearch,
  onSearchChange,
  onCloseSearchEdit,
  align = 'left',
  className = '',
  compact = false,
  showSort = true,
  showSearch = true,
  style
}) => {
  const labelText = normalizeLabel(label);
  const trimmedSearch = String(searchValue || '').trim();
  const hasFilter = trimmedSearch.length > 0;
  const alignClass = alignClassMap[align] || alignClassMap.left;
  const justifyClass = justifyClassMap[align] || '';
  const iconSize = compact ? 'w-3 h-3' : 'w-3.5 h-3.5';
  const inputMax = compact ? 'max-w-[100px]' : 'max-w-[150px]';

  const renderLabelContent = () => {
    if (Array.isArray(label)) {
      return label.map((line, index) => (
        <span key={`${line}-${index}`} className={index > 0 ? 'block' : undefined}>
          {line}
        </span>
      ));
    }

    if (typeof label === 'string' && label.includes('\n')) {
      return label.split('\n').map((line, index) => (
        <span key={`${line}-${index}`} className={index > 0 ? 'block' : undefined}>
          {line}
        </span>
      ));
    }

    return labelText;
  };

  return (
    <th
      className={`${className} ${alignClass} relative group`.trim()}
      style={style}
    >
      {isSearchActive ? (
        <div className={`flex items-center gap-1 w-full min-w-0 ${justifyClass}`}>
          <input
            type="text"
            value={searchValue || ''}
            onChange={(e) => onSearchChange?.(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                onCloseSearchEdit?.();
              }
            }}
            autoFocus
            placeholder={`${labelText} ara...`}
            className={`w-auto min-w-[80px] ${inputMax} px-2 py-1 text-xs border border-emerald-500 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500`}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClearSearch?.();
            }}
            className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            title="Filtreyi temizle"
          >
            <X className={iconSize} />
          </button>
        </div>
      ) : hasFilter ? (
        <div className={`flex items-center gap-1 w-full min-w-0 ${justifyClass}`}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpenSearch?.();
            }}
            className="inline-flex items-center gap-1 max-w-full min-w-0 px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-500 text-emerald-700 dark:text-emerald-300 text-[11px] font-medium hover:bg-emerald-100 dark:hover:bg-emerald-900/50"
            title="Filtreyi düzenle"
          >
            <span className="truncate">{trimmedSearch}</span>
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClearSearch?.();
            }}
            className="flex-shrink-0 p-0.5 rounded-full text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/40"
            title="Filtreyi temizle"
          >
            <X className={iconSize} />
          </button>
        </div>
      ) : (
        <div className={`flex items-center gap-1 ${justifyClass}`}>
          <span
            className={`${align === 'right' || align === 'center' ? '' : 'flex-1'} cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 select-none`}
            onClick={() => onSort?.()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSort?.();
              }
            }}
            role={onSort ? 'button' : undefined}
            tabIndex={onSort ? 0 : undefined}
          >
            {renderLabelContent()}
          </span>
          {(showSort || showSearch) && (
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {showSort && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSort?.();
                  }}
                  className="p-0.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                  title="Sırala"
                >
                  {isSorted ? (
                    sortDirection === 'asc' ? (
                      <ChevronUp className={iconSize} />
                    ) : (
                      <ChevronDown className={iconSize} />
                    )
                  ) : (
                    <ChevronUp className={`${iconSize} opacity-50`} />
                  )}
                </button>
              )}
              {showSearch && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenSearch?.();
                  }}
                  className="p-0.5 text-gray-400 hover:text-green-600 dark:hover:text-green-400"
                  title="Ara"
                >
                  <Search className={iconSize} />
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </th>
  );
};

export default ColumnSearchHeaderCell;
